import { Form, FormikProvider, useFormik } from 'formik';
import { CircularProgress, FormHelperText, Grid, MenuItem, Select, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { useTranslation } from 'react-i18next';
import { DeliveryConfiguration } from '../../@types/model/deliveryConfiguration';
import DeviceTypePicker from 'components/DeviceTypePicker';
import { useSelector, RootState } from '../../redux/store';
import EmailsInput from 'components/EmailsInput';

export interface DeliveryConfigurationFormProps {
  deliveryConfiguration: DeliveryConfiguration;
  edit: boolean;
  submitHandler: (group: DeliveryConfiguration) => void;
  loading: boolean;
}

type createOrUpdateDeliveryConfigurationInitialValues = {
  type: string;
  units: string;
  name: string;
  slots: number;
  slot_amount: number;
  min: number;
  max: number;
  device_type: string;
  notify_to: string[];
};

const DeliveryConfigurationForm = (props: DeliveryConfigurationFormProps) => {
  const isMountedRef = useIsMountedRef();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const { workspace } = useSelector((state: RootState) => state.workspace);

  const createOrUpdateDeliveryConfigurationShchema = Yup.object().shape({
    name: Yup.string().required(t('El nombre es necesario'))
  });

  const CreateOrUpdateDeliveryConfiguration =
    useFormik<createOrUpdateDeliveryConfigurationInitialValues>({
      initialValues: {
        type: props.deliveryConfiguration.type,
        units: props.deliveryConfiguration.units,
        name: props.deliveryConfiguration.configuration.name,
        slots: props.deliveryConfiguration.configuration.slots,
        slot_amount: props.deliveryConfiguration.configuration.slot_amount,
        min: props.deliveryConfiguration.configuration.min,
        max: props.deliveryConfiguration.configuration.max,
        device_type: props.deliveryConfiguration.configuration.device_type,
        notify_to: props.deliveryConfiguration.notify_to
      },
      validationSchema: createOrUpdateDeliveryConfigurationShchema,
      onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
        try {
          //await chagePassword(values.email, values.password, values.newPassword);
          props.submitHandler({
            guid: props.deliveryConfiguration.guid,
            type: values.type,
            user: user?.id,
            units: values.units,
            workspace: workspace?.wsName,
            device_types: [],
            notify_to: values.notify_to,
            configuration: {
              name: values.name,
              slots: values.slots,
              slot_amount: values.slot_amount,
              min: values.min,
              max: values.max,
              device_type: values.device_type
            }
          } as DeliveryConfiguration);
          if (isMountedRef.current) {
            setSubmitting(false);
          }
        } catch (error: any) {
          resetForm();
          if (isMountedRef.current) {
            setSubmitting(false);
          }
        }
      }
    });
  return (
    <FormikProvider value={CreateOrUpdateDeliveryConfiguration}>
      <Form
        autoComplete="off"
        noValidate
        onSubmit={CreateOrUpdateDeliveryConfiguration.handleSubmit}
      >
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              autoComplete={t('name')}
              type="string"
              label={t('name')}
              {...CreateOrUpdateDeliveryConfiguration.getFieldProps('name')}
              error={Boolean(
                CreateOrUpdateDeliveryConfiguration.touched.name &&
                  CreateOrUpdateDeliveryConfiguration.errors.name
              )}
              helperText={
                CreateOrUpdateDeliveryConfiguration.touched.name &&
                CreateOrUpdateDeliveryConfiguration.errors.name
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Select
              {...CreateOrUpdateDeliveryConfiguration.getFieldProps('type')}
              label={t('type')}
              error={Boolean(
                CreateOrUpdateDeliveryConfiguration.touched.type &&
                  CreateOrUpdateDeliveryConfiguration.errors.type
              )}
              style={{ width: '100%' }}
            >
              <MenuItem value={'bulk'}>{t('bulk')}</MenuItem>
              <MenuItem value={'fixed'}>{t('fixed')}</MenuItem>
            </Select>
            <FormHelperText>
              {CreateOrUpdateDeliveryConfiguration.touched.type &&
                CreateOrUpdateDeliveryConfiguration.errors.type}
            </FormHelperText>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              autoComplete={t('unit')}
              type="string"
              label={t('unit')}
              {...CreateOrUpdateDeliveryConfiguration.getFieldProps('units')}
              error={Boolean(
                CreateOrUpdateDeliveryConfiguration.touched.units &&
                  CreateOrUpdateDeliveryConfiguration.errors.units
              )}
              helperText={
                CreateOrUpdateDeliveryConfiguration.touched.units &&
                CreateOrUpdateDeliveryConfiguration.errors.units
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DeviceTypePicker
              deviceTypePreset={CreateOrUpdateDeliveryConfiguration.values.device_type}
              changeHandler={(value) =>
                CreateOrUpdateDeliveryConfiguration.setFieldValue('device_type', value)
              }
              deviceTypeAvailable={[]}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              autoComplete={t('slots')}
              type="number"
              label={t('slots')}
              {...CreateOrUpdateDeliveryConfiguration.getFieldProps('slots')}
              error={Boolean(
                CreateOrUpdateDeliveryConfiguration.touched.slots &&
                  CreateOrUpdateDeliveryConfiguration.errors.slots
              )}
              helperText={
                CreateOrUpdateDeliveryConfiguration.touched.slots &&
                CreateOrUpdateDeliveryConfiguration.errors.slots
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              autoComplete={t('slot_amount')}
              type="number"
              label={t('slot_amount')}
              {...CreateOrUpdateDeliveryConfiguration.getFieldProps('slot_amount')}
              error={Boolean(
                CreateOrUpdateDeliveryConfiguration.touched.slot_amount &&
                  CreateOrUpdateDeliveryConfiguration.errors.slot_amount
              )}
              helperText={
                CreateOrUpdateDeliveryConfiguration.touched.slot_amount &&
                CreateOrUpdateDeliveryConfiguration.errors.slot_amount
              }
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              autoComplete={t('min_slots')}
              type="number"
              label={t('min_slots')}
              {...CreateOrUpdateDeliveryConfiguration.getFieldProps('min')}
              error={Boolean(
                CreateOrUpdateDeliveryConfiguration.touched.min &&
                  CreateOrUpdateDeliveryConfiguration.errors.min
              )}
              helperText={
                CreateOrUpdateDeliveryConfiguration.touched.min &&
                CreateOrUpdateDeliveryConfiguration.errors.min
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              autoComplete={t('max_slots')}
              type="number"
              label={t('max_slots')}
              {...CreateOrUpdateDeliveryConfiguration.getFieldProps('max')}
              error={Boolean(
                CreateOrUpdateDeliveryConfiguration.touched.max &&
                  CreateOrUpdateDeliveryConfiguration.errors.max
              )}
              helperText={
                CreateOrUpdateDeliveryConfiguration.touched.max &&
                CreateOrUpdateDeliveryConfiguration.errors.max
              }
              inputProps={{
                min: '0',
                max: CreateOrUpdateDeliveryConfiguration.values.slots,
                step: '1'
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <h3>{t('emails_list')}</h3>
            <EmailsInput
              emails={CreateOrUpdateDeliveryConfiguration.values.notify_to}
              changeHandler={(emails: string[]) => {
                CreateOrUpdateDeliveryConfiguration.setFieldValue('notify_to', emails);
              }}
            />
          </Grid>
        </Grid>
        <LoadingButton
          disabled={props.loading}
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={CreateOrUpdateDeliveryConfiguration.isSubmitting}
          sx={{ my: 2 }}
        >
          {props.loading ? <CircularProgress size={26} /> : props.edit ? t('update') : t('add')}
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
};

export default DeliveryConfigurationForm;
